/** @type {import('axios').AxiosInstance} */
import { apiInstance } from "./axios";
import { LOCALSTORAGE } from "./constants";

let isRefreshing = false;
let failedQueue = [];
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 2;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export const setupAxiosInterceptors = (
  isAdmin = false,
  isContractor = false,
  onTokenRefreshed = null
) => {
  apiInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Don't retry auth-related endpoints
      const isAuthEndpoint = originalRequest.url?.includes("/auth/");
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      // If error is not 401 or request already retried, reject
      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // If already refreshing, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return apiInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // Check if we've exceeded max refresh attempts
      if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        processQueue(new Error("Max refresh attempts reached"), null);
        clearAuthData(isAdmin, isContractor);
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      isRefreshing = true;
      refreshAttempts++;

      try {
        const refreshUrl = isAdmin
          ? "api/admin/auth/refresh"
          : isContractor
          ? "api/contractor/auth/refresh"
          : "api/user/auth/refresh";

        // Get the current token from localStorage
        const currentTokenKey = isAdmin
          ? LOCALSTORAGE.ACCESS_TOKEN
          : isContractor
          ? LOCALSTORAGE.CONTRACTOR_ACCESS_TOKEN
          : LOCALSTORAGE.USER_ACCESS_TOKEN;
        const currentToken = localStorage.getItem(currentTokenKey);

        if (!currentToken) {
          throw new Error("No token available for refresh");
        }

        // Make refresh request with current token
        const response = await apiInstance.post(
          refreshUrl,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );

        const {
          access_token,
          user: userData,
          company: companyData,
        } = response.data;

        if (!access_token) {
          throw new Error("No access token received from refresh");
        }

        // Store new token
        localStorage.setItem(currentTokenKey, access_token);

        // Update axios default header
        apiInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${access_token}`;

        // Update company ID if provided (for admin)
        if (isAdmin) {
          let companyIdToSet = null;

          if (companyData?.company_id) {
            companyIdToSet = companyData.company_id;
          } else if (userData?.company_id) {
            companyIdToSet = userData.company_id;
          }

          if (companyIdToSet) {
            localStorage.setItem(
              LOCALSTORAGE.COMPANY_ID,
              companyIdToSet.toString()
            );
            apiInstance.defaults.headers.common["X-Company-ID"] =
              companyIdToSet.toString();
          }

          // Update user ID if provided
          if (userData?.user_id || userData?.id) {
            const userIdToSet = userData.user_id || userData.id;
            localStorage.setItem(LOCALSTORAGE.USER_ID, userIdToSet.toString());
          }
        }

        // Call the callback to update the store's reactive state
        if (onTokenRefreshed && typeof onTokenRefreshed === "function") {
          try {
            onTokenRefreshed({
              access_token,
              user: userData,
              company: companyData,
            });
          } catch (callbackError) {
            console.error("Error in token refresh callback:", callbackError);
            // Don't fail the refresh if callback fails
          }
        }

        // Reset refresh attempts counter on success
        refreshAttempts = 0;

        // Process queued requests
        processQueue(null, access_token);

        // Update original request header
        originalRequest.headers["Authorization"] = `Bearer ${access_token}`;

        // Update X-Company-ID header for original request if it was present
        if (originalRequest.headers["X-Company-ID"]) {
          const storedCompanyId = localStorage.getItem(LOCALSTORAGE.COMPANY_ID);
          if (storedCompanyId) {
            originalRequest.headers["X-Company-ID"] = storedCompanyId;
          }
        }

        return apiInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthData(isAdmin, isContractor);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

// Helper to clear auth data and redirect to the correct login page
const clearAuthData = (isAdmin, isContractor) => {
  const tokenKey = isAdmin
    ? LOCALSTORAGE.ACCESS_TOKEN
    : isContractor
    ? LOCALSTORAGE.CONTRACTOR_ACCESS_TOKEN
    : LOCALSTORAGE.USER_ACCESS_TOKEN;

  localStorage.removeItem(tokenKey);
  delete apiInstance.defaults.headers.common["Authorization"];

  const loginPath = isAdmin
    ? "/admin/login"
    : isContractor
    ? "/contractor/login"
    : "/login";
  window.location.href = loginPath;
};

export const setupRefreshTimer = (token, refreshCallback, isAdmin = false) => {
  if (!token) return null;

  try {
    // Parse token
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = window.atob(base64);
    const decoded = JSON.parse(jsonPayload);

    if (!decoded || !decoded.exp) return null;

    // Get current time in seconds
    const currentTime = Math.floor(Date.now() / 1000);

    // If token is already expired, don't set up refresh
    if (decoded.exp <= currentTime) {
      // console.log("Token already expired, not setting refresh timer");
      return null;
    }

    // Calculate time until expiry in milliseconds
    const timeUntilExpiry = (decoded.exp - currentTime) * 1000;

    // Refresh 5 minutes before expiry or halfway through the token's lifetime, whichever is smaller
    const tokenLifetime = (decoded.exp - decoded.iat) * 1000;
    const refreshBuffer = Math.min(5 * 60 * 1000, tokenLifetime / 2);
    const refreshDelay = Math.max(0, timeUntilExpiry - refreshBuffer);

    // console.log(`Setting up refresh timer for ${refreshDelay}ms from now`);

    // Set up refresh timer
    return setTimeout(async () => {
      try {
        // console.log("Executing token refresh");
        await refreshCallback();
      } catch (error) {
        // console.error("Error in refresh timer callback:", error);
        // Clear auth data and redirect on failure
        const tokenKey = isAdmin
          ? LOCALSTORAGE.ACCESS_TOKEN
          : LOCALSTORAGE.USER_ACCESS_TOKEN;
        localStorage.removeItem(tokenKey);
        delete apiInstance.defaults.headers.common["Authorization"];

        const loginPath = isAdmin ? "/admin/login" : "/login";
        window.location.href = loginPath;
      }
    }, refreshDelay);
  } catch (error) {
    // console.error("Error setting up refresh timer:", error);
    return null;
  }
};
